import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Link,
  withRouter,
  matchPath,
} from 'react-router-dom';
import classnames from 'classnames';
import {
  Badge,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import { useIntl } from 'react-intl';
import $ from 'jquery';

import AddMenu from '../AddMenu';
import MoreMenu from '../MoreMenu';
import NotificationsDropdown from '../NotificationsDropdown';
import ProfileMenu from '../ProfileMenu';
import SwitchAccountSwitch from '../SwitchAccountSwitch';
import SwitchUserSwitch from '../SwitchUserSwitch';
import UpgradePlanButton from '../UpgradePlanButton';

import {
  userHasCap,
  userIsAdmin,
  userIsManager,
} from '../../helpers/Auth';
import { getSetting, getUserSetting } from '../../helpers/Settings';
import { getInitialOpenOnboardingStep } from '../../helpers/Onboarding';
import { upgradeModalIfNeeded } from '../../helpers/UpgradePlan';

const SidebarNavigation = ({
  location,
  profile,
  expanded,
  toggleNavigation,
}) => {
  const intl = useIntl();

  const handleUpdateSidebar = useCallback(() => {
    const el = $('.sidebar-navigation');
    if (el.get(0) && el.get(0).scrollWidth < el.width()) {
      el.css({ 'min-width': 80 * 2 - el.get(0).scrollWidth + 2 });
    } else {
      el.css({ 'min-width': 'auto' });
    }
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleUpdateSidebar);
    const sidebar = document.querySelector('.layout');
    resizeObserver.observe(sidebar);

    return () => {
      resizeObserver.unobserve(sidebar);
    };
  }, [handleUpdateSidebar]);

  if (!profile) {
    return null;
  }

  const { pathname } = location;

  const dashboardUrl = getSetting('home_page') && getSetting('home_page') !== 'dashboard' ? '/dashboard' : '/';

  const onboardingDismissed = getUserSetting('onboarding_dismissed');
  const showDashboardBadge = !onboardingDismissed && getInitialOpenOnboardingStep();

  return (
    <div className={classnames('sidebar-navigation', { expanded })}>
      <div className="sidebar-navigation-top">
        <Nav className="ml-auto" navbar>
          <SwitchAccountSwitch />
          <NavItem className={classnames({ active: pathname === '/' })}>
            <NavLink tag={Link} to={dashboardUrl} onClick={toggleNavigation}>
              <span className="nav-item-icon">
                <i className="far fa-tachometer-alt-fast" />
              </span>
              {showDashboardBadge && (
                <Badge color="primary" pill className="sidebar-badge-small">{' '}</Badge>
              )}
              <span className="nav-item-title">
                {intl.formatMessage({ defaultMessage: 'Dashboard' })}
              </span>
            </NavLink>
          </NavItem>
          {userHasCap('share') && (
            <NavItem className={classnames({ active: pathname === '/share-links' })}>
              <NavLink tag={Link} to="/share-links" onClick={toggleNavigation}>
                <span className="nav-item-icon">
                  <i className="far fa-share-alt" />
                </span>
                <span className="nav-item-title">
                  {intl.formatMessage({ defaultMessage: 'Share links' })}
                </span>
              </NavLink>
            </NavItem>
          )}
          {(userIsAdmin() || userIsManager() || userHasCap('upload_pages')) && (
            <NavItem className={classnames('nav-item-upload-pages', { active: pathname === '/admin/upload-pages' })}>
              <NavLink
                tag={Link}
                to="/admin/upload-pages"
                onClick={(e) => upgradeModalIfNeeded('upload-pages', () => toggleNavigation(), e)}
              >
                <span className="nav-item-icon">
                  <i className="far fa-arrow-up-from-bracket" />
                </span>
                <span className="nav-item-title">
                  {intl.formatMessage({ defaultMessage: 'Guest uploads' })}
                </span>
              </NavLink>
              <UpgradePlanButton
                feature="upload-pages"
                simple={true}
              />
            </NavItem>
          )}
          {userIsAdmin() && (
            <NavItem className={classnames({ active: pathname === '/admin/duplicates' })}>
              <NavLink
                tag={Link}
                to="/admin/duplicates"
                onClick={(e) => upgradeModalIfNeeded('duplicate-files', () => toggleNavigation(), e)}
              >
                <span className="nav-item-icon">
                  <i className="far fa-copy" />
                </span>
                <span className="nav-item-title">
                  {intl.formatMessage({ defaultMessage: 'Duplicates' })}
                </span>
              </NavLink>
              <UpgradePlanButton
                feature="duplicate-files"
                simple={true}
              />
            </NavItem>
          )}
          {((userIsAdmin() ||
            userHasCap('request_download_files_and_collections')
          ) && getSetting('download_requests_enabled')) && (
            <NavItem className={classnames({ active: !!matchPath(pathname, ['/download-requests', '/download-requests/:uuid']) })}>
              <NavLink tag={Link} to="/download-requests" onClick={toggleNavigation}>
                <span className="nav-item-icon">
                  <i className="far fa-cart-arrow-down" />
                </span>
                <span className="nav-item-title">
                  {intl.formatMessage({ defaultMessage: 'Download requests' })}
                </span>
              </NavLink>
            </NavItem>
          )}
          {(getSetting('cs1') && userIsAdmin()) && (
            <NavItem className={classnames({ active: pathname === '/admin/users' })}>
              <NavLink tag={Link} to="/admin/users" onClick={toggleNavigation}>
                <span className="nav-item-icon">
                  <i className="far fa-users" />
                </span>
                <span className="nav-item-title">
                  {intl.formatMessage({ defaultMessage: 'Users' })}
                </span>
              </NavLink>
            </NavItem>
          )}
          <MoreMenu toggleNavigation={toggleNavigation} />
        </Nav>
      </div>
      <div className="sidebar-navigation-bottom">
        <Nav className="ml-auto" navbar>
          <NotificationsDropdown />
          <AddMenu toggleNavigation={toggleNavigation} />
          <SwitchUserSwitch />
          <ProfileMenu toggleNavigation={toggleNavigation} />
        </Nav>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

SidebarNavigation.propTypes = {
  location: PropTypes.object.isRequired,
  profile: PropTypes.object,
  expanded: PropTypes.bool.isRequired,
  toggleNavigation: PropTypes.func.isRequired,

};

SidebarNavigation.defaultProps = {
  profile: null,
};

export default withRouter(connect(mapStateToProps)(SidebarNavigation));
